import { API_BASE_URL } from './axios'

export interface StreamErrorInfo {
  status?: number
  fatal: boolean
}

export interface EventStreamHandlers {
  // Named events this caller cares about, keyed by event name. The payload is
  // whatever JSON.parse returned — cast it at the call site.
  events: Record<string, (data: unknown) => void>
  onOpen?: () => void
  onError?: (info: StreamErrorInfo) => void
  onAuthRevoked?: () => void
}

const INITIAL_RECONNECT_DELAY_MS = 2000

// The server replays the last 60 seconds of events after auth_ok, which is what
// makes a reconnect lossless. A backoff longer than that window would land past
// the replay and drop events silently, so this ceiling is load-bearing rather
// than a tuning knob. See src/lib/sse.test.ts.
export const MAX_RECONNECT_DELAY_MS = 30_000

// The server force-reconnects on a 10 minute connection age cap; that path is a
// fixed short delay, not the backoff, so a healthy stream never drifts upward.
const SERVER_RECONNECT_DELAY_MS = 1000

// Both are server-side capacity signals rather than faults, so they wait a fixed
// interval instead of entering backoff.
const ACTIVE_STREAM_LIMIT_DELAY_MS = 30_000
const STREAM_CAPACITY_DELAY_MS = 15_000

// The token is read at every connect, not captured once — a reconnect after a
// silent refresh must carry the new one. EventSource cannot set headers, which
// is why it rides in the query string.
function urlWithToken(path: string): string {
  const token = sessionStorage.getItem('auth_token')
  const base = API_BASE_URL.replace(/\/$/, '')
  const separator = path.includes('?') ? '&' : '?'
  return `${base}${path}${separator}access_token=${encodeURIComponent(token ?? '')}`
}

/**
 * Opens an SSE stream with reconnect, backoff, and the shared auth framing
 * (`auth_ok` / `reconnect` / `auth_revoked` / `error`). Callers supply only
 * their own named events.
 *
 * Returns an unsubscribe function; calling it stops reconnecting permanently.
 */
export function openEventStream(path: string, handlers: EventStreamHandlers): () => void {
  let reconnectDelay = INITIAL_RECONNECT_DELAY_MS
  let stopped = false
  let currentEs: EventSource | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null

  function connect(): void {
    if (stopped) return

    const es = new EventSource(urlWithToken(path))
    currentEs = es

    let closedThisInstance = false
    function closeOnce(): boolean {
      if (closedThisInstance) return true
      closedThisInstance = true
      es.close()
      currentEs = null
      return false
    }

    es.addEventListener('auth_ok', () => {
      if (stopped) return
      reconnectDelay = INITIAL_RECONNECT_DELAY_MS
      handlers.onOpen?.()
    })

    for (const [name, handler] of Object.entries(handlers.events)) {
      es.addEventListener(name, (event) => {
        if (stopped) return
        try {
          handler(JSON.parse((event as MessageEvent).data))
        } catch (err) {
          console.error(`Failed to parse ${name} SSE event`, err)
        }
      })
    }

    es.addEventListener('reconnect', () => {
      if (stopped || closeOnce()) return
      reconnectTimer = setTimeout(connect, SERVER_RECONNECT_DELAY_MS)
    })

    es.addEventListener('auth_revoked', () => {
      stopped = true
      closeOnce()
      handlers.onAuthRevoked?.()
    })

    es.addEventListener('error', (event) => {
      if (stopped || closeOnce()) return
      try {
        const { code } = JSON.parse((event as MessageEvent).data ?? '{}')
        if (code === 'auth_failed') {
          handlers.onError?.({ status: 401, fatal: true })
        } else if (code === 'active_stream_limit') {
          handlers.onError?.({ fatal: false })
          reconnectTimer = setTimeout(connect, ACTIVE_STREAM_LIMIT_DELAY_MS)
        } else if (code === 'stream_capacity_reached') {
          handlers.onError?.({ fatal: false })
          reconnectTimer = setTimeout(connect, STREAM_CAPACITY_DELAY_MS)
        } else {
          reconnectWithBackoff()
        }
      } catch {
        reconnectWithBackoff()
      }
    })

    es.onerror = () => {
      if (stopped || closeOnce()) return
      reconnectWithBackoff()
    }
  }

  function reconnectWithBackoff(): void {
    if (stopped) return
    handlers.onError?.({ fatal: false })
    reconnectTimer = setTimeout(connect, reconnectDelay)
    reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY_MS)
  }

  connect()

  return () => {
    stopped = true
    if (reconnectTimer) clearTimeout(reconnectTimer)
    currentEs?.close()
    currentEs = null
  }
}
